/**
 * Đối soát tiền SaaS và điều chỉnh ví — API của console nền tảng (#18990 P2.3).
 * Real /api/v1/platform/billing.
 *
 * Đối soát **chỉ báo, không tự sửa** (khuôn D-94). Một job tự chỉnh số dư là một job có thể tự
 * chỉnh SAI, lúc 3 giờ sáng, và để lại một hệ thống mà không ai nói được con số nào từng là con
 * số nào.
 */
import { apiClient } from "./client";

export type Severity = "CRITICAL" | "WARNING";

export interface ReconciliationFinding {
  check: string;
  tenantId: string | null;
  detail: string;
  amountVnd: number | null;
  severity: Severity;
  /** Ai phải xử lý. Một báo cáo toàn số sẽ được đọc đúng một lần rồi thôi. */
  owner: string;
  /** Làm gì. Đây là phần khiến báo cáo có ích. */
  action: string;
}

export interface ReconciliationReport {
  findings: ReconciliationFinding[];
  countsByCheck: Record<string, number>;
}

export interface WalletEntry {
  id: string;
  type: string;
  amountVnd: number;
  balanceAfterVnd: number;
  sourceRef: string | null;
  note: string | null;
  createdAt: string;
}

const BASE = "/v1/platform/billing";

export const platformBillingApi = {
  reconciliation(): Promise<ReconciliationReport> {
    return apiClient.get<ReconciliationReport>(`${BASE}/reconciliation`).then((r) => r.data);
  },
  walletOf(tenantId: string): Promise<WalletEntry[]> {
    return apiClient
      .get<WalletEntry[]>(`${BASE}/wallet`, { params: { tenantId } })
      .then((r) => r.data);
  },
  /** Lý do BẮT BUỘC. Một thay đổi số dư không giải thích được sau vài tháng là thứ không phân biệt nổi với lỗi. */
  adjust(tenantId: string, amountVnd: number, reason: string): Promise<WalletEntry> {
    return apiClient
      .post<WalletEntry>(`${BASE}/wallet/adjust`, { tenantId, amountVnd, reason })
      .then((r) => r.data);
  },
};

export const platformBillingQueryKeys = {
  reconciliation: () => ["platform-billing", "reconciliation"] as const,
};

// =========================================================== Giá theo hợp đồng

/** Một mức giá đã thoả thuận cho một phòng khám, một gói, trong một khoảng thời gian. */
export interface TenantPlanPrice {
  id: string;
  tenantId: string;
  planId: string;
  priceVnd: number;
  effectiveFrom: string;
  /** null = còn hiệu lực. Giá cũ không bị xoá, chỉ bị đóng lại. */
  effectiveTo: string | null;
  source: string;
  contractRef: string | null;
  version: number;
}

export interface PlanPriceApplied {
  applied: boolean;
  reason: string | null;
  price: TenantPlanPrice | null;
}

export interface SetPlanPriceInput {
  tenantId: string;
  planCode: string;
  priceVnd: number;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  source?: string | null;
  contractRef?: string | null;
  version?: number | null;
}

export const planPriceApi = {
  history(tenantId: string): Promise<TenantPlanPrice[]> {
    return apiClient
      .get<TenantPlanPrice[]>(`${BASE}/plan-price`, { params: { tenantId } })
      .then((r) => r.data);
  },
  set(input: SetPlanPriceInput): Promise<PlanPriceApplied> {
    return apiClient.post<PlanPriceApplied>(`${BASE}/plan-price`, input).then((r) => r.data);
  },
};

export const planPriceQueryKeys = {
  history: (tenantId: string) => ["platform-billing", "plan-price", tenantId] as const,
};

// ============================================================ Hoá đơn GTGT

/** Một khoản đã thu tiền nhưng hoá đơn chưa xong. */
export interface InvoiceQueueRow {
  id: string;
  tenantId: string;
  tenantCode: string | null;
  /** Rỗng khi hoá đơn ghi nhận một khoản NẠP VÍ — khoản đó không gắn với kỳ nào. */
  periodId: string | null;
  paymentIntentId: string | null;
  amountVnd: number;
  buyerName: string | null;
  /** Không có mã số thuế thì không nhà cung cấp nào xuất được hoá đơn GTGT. */
  buyerTaxCode: string | null;
  buyerAddress: string | null;
  buyerEmail: string | null;
  /** SUBMITTED = đã gửi nhà cung cấp, chưa có số hoá đơn. Không gửi lại, chỉ tra số. */
  status: "PENDING" | "SUBMITTED" | "BLOCKED";
  provider: string | null;
  lookupCode: string | null;
  periodStarts: string | null;
  periodEnds: string | null;
  createdAt: string;
}

export interface RecordInvoiceInput {
  status: "ISSUED" | "SUBMITTED" | "NOT_REQUIRED" | "BLOCKED" | "PENDING";
  provider?: string | null;
  invoiceNo?: string | null;
  serial?: string | null;
  lookupCode?: string | null;
  note?: string | null;
}

export const saasInvoiceApi = {
  queue(): Promise<InvoiceQueueRow[]> {
    return apiClient.get<InvoiceQueueRow[]>(`${BASE}/invoices`).then((r) => r.data);
  },
  /** Đọc lại tên/MST/địa chỉ từ hồ sơ phòng khám — nửa còn lại của trạng thái BLOCKED. */
  refreshBuyer(id: string): Promise<void> {
    return apiClient.post(`${BASE}/invoices/${id}/refresh-buyer`).then(() => undefined);
  },
  /** 409 khi dòng đã phát hành — số hoá đơn đã ra ngoài thì không ghi đè bằng một cú bấm. */
  record(id: string, input: RecordInvoiceInput): Promise<void> {
    return apiClient.post(`${BASE}/invoices/${id}/record`, input).then(() => undefined);
  },
};

export const saasInvoiceQueryKeys = {
  queue: () => ["platform-billing", "invoices"] as const,
};

// ============================================================ Hoá đơn điện tử (PA Vietnam)

/**
 * Tài khoản hoá đơn điện tử của Addy như màn hình nhìn thấy (#18990).
 *
 * Không có ô nào chở client secret: `hasClientSecret` nói đã khai hay chưa, và đó là tất cả những
 * gì màn hình cần biết. `missing` là danh sách do chính lớp nhà cung cấp trả về, nên nó không bao
 * giờ lệch với thứ PA thực sự đòi.
 */
export interface EInvoiceConfigRow {
  id: string;
  provider: string;
  baseUrl: string;
  clientId: string;
  hasClientSecret: boolean;
  scope: string | null;
  idPartner: number | null;
  invoiceType: number | null;
  /** Mẫu số, ví dụ 1/001. */
  templateName: string | null;
  /** Ký hiệu, ví dụ C26TAA. */
  serial: string | null;
  status: "DRAFT" | "ACTIVE" | "DISABLED";
  missing: string[];
  activatedAt: string | null;
  updatedAt: string;
}

/** Ô để trống nghĩa là GIỮ NGUYÊN — không ai gõ lại thứ họ không nhìn thấy. */
export interface EInvoiceConfigInput {
  baseUrl?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  scope?: string | null;
  idPartner?: number | null;
  invoiceType?: number | null;
  templateName?: string | null;
  serial?: string | null;
}

/** Kết quả một lần thử phát hành, theo TỪNG hoá đơn. */
export interface IssueOutcome {
  invoiceId: string;
  status: "ISSUED" | "SUBMITTED" | "PENDING" | "BLOCKED" | "UNCHANGED";
  detail: string;
}

const EINVOICE = `${BASE}/einvoice`;

export const eInvoiceApi = {
  config(): Promise<EInvoiceConfigRow[]> {
    return apiClient.get<EInvoiceConfigRow[]>(`${EINVOICE}/config`).then((r) => r.data);
  },
  save(id: string | null, config: EInvoiceConfigInput): Promise<EInvoiceConfigRow> {
    return apiClient
      .post<EInvoiceConfigRow>(`${EINVOICE}/config`, { id, config })
      .then((r) => r.data);
  },
  activate(id: string): Promise<EInvoiceConfigRow> {
    return apiClient
      .post<EInvoiceConfigRow>(`${EINVOICE}/config/${id}/activate`)
      .then((r) => r.data);
  },
  issuePending(): Promise<IssueOutcome[]> {
    return apiClient.post<IssueOutcome[]>(`${EINVOICE}/issue-pending`).then((r) => r.data);
  },
  issueOne(invoiceId: string): Promise<IssueOutcome> {
    return apiClient
      .post<IssueOutcome>(`${EINVOICE}/invoices/${invoiceId}/issue`)
      .then((r) => r.data);
  },
};

export const eInvoiceQueryKeys = {
  config: () => ["platform-billing", "einvoice", "config"] as const,
};

// ============================================================ Bảng giá gói

/** Một gói dịch vụ như màn hình nền tảng nhìn thấy (#18990). */
export interface PlanCatalogRow {
  id: string;
  code: string;
  name: string;
  monthlyPriceVnd: number;
  /** Phòng khám có tự mua được không. Trước đây là một Set cứng trong code. */
  sellable: boolean;
  sortOrder: number;
  /**
   * Số tính năng gói cho dùng — đúng con số "Đã chọn X/…" của bảng tick (#19674), không phải số
   * dòng quyền lợi thô (vốn tính cả `module.*`/`limit.*` và mã màn hình đã gỡ).
   */
  screenCount: number;
  /** Bao nhiêu phòng khám đang ở gói này — để biết mình đang động vào cái gì. */
  tenantsOnPlan: number;
  /**
   * Doanh thu qua CỔNG CÓ PHÍ mà gói bao mỗi tháng (#18998). `null` = gói không bao, phòng khám
   * tự trả phí cổng. Tiền vào bằng VietQR không tính — nó không tốn của Addy đồng nào.
   *
   * `undefined` là chuyện thứ BA và không được gộp vào hai chuyện trên: máy chủ không gửi trường
   * này. Đó đúng là cái đã xảy ra — DTO tầng ứng dụng thiếu trường, nên `GET` không bao giờ trả
   * trần về trong khi chỗ này vẫn khai là có (#19052). Khai `?` để TypeScript bắt màn hình phải
   * xử lý khả năng đó, thay vì tin rằng `null` là câu trả lời duy nhất cho "không có số nào".
   */
  onlinePaymentCapVnd?: number | null;
}

/**
 * Bản vá một gói — chỉ trường nào có mặt mới đổi.
 *
 * **Quy ước `-1` đã bị bỏ (#19052).** Trần có ba ý định: giữ nguyên, đặt về một số, gỡ hẳn. Bản cũ
 * nhồi cả ba vào một con số, nên "gói này không đặt trần" và "biểu mẫu không gửi trường này" gửi đi
 * y hệt nhau — và vì `GET` không trả trần về, ô nhập luôn rỗng, nên mỗi lần đổi TÊN gói là một lần
 * gửi `-1` và xoá trần. Nay giá trị và ý định gỡ đi trong hai trường riêng, và số âm bị máy chủ từ
 * chối bằng 400 thay vì âm thầm xoá.
 */
export interface PlanCatalogUpdate {
  name?: string;
  monthlyPriceVnd?: number;
  sellable?: boolean;
  sortOrder?: number;
  /** Bỏ qua = KHÔNG đổi. Số >= 0 = mức trần. Số âm bị từ chối (400). */
  onlinePaymentCapVnd?: number;
  /** `true` = gỡ trần, gói KHÔNG bao phí cổng. Bỏ qua = không đổi. Phải nói ra mới xảy ra. */
  clearOnlinePaymentCap?: boolean;
}

/** Gói mới (ADR-0053). Mã không sửa được sau khi tạo; giá 0đ là gói miễn phí. */
export interface NewPlanInput {
  code: string;
  name: string;
  monthlyPriceVnd: number;
}

export const planCatalogApi = {
  list(): Promise<PlanCatalogRow[]> {
    return apiClient.get<PlanCatalogRow[]>("/v1/platform/plans").then((r) => r.data);
  },
  /** Gói tạo ra CHƯA mở bán và mới chỉ có các màn hình khoá. */
  create(body: NewPlanInput): Promise<PlanCatalogRow> {
    return apiClient.post<PlanCatalogRow>("/v1/platform/plans", body).then((r) => r.data);
  },
  update(id: string, body: PlanCatalogUpdate): Promise<PlanCatalogRow> {
    return apiClient.put<PlanCatalogRow>(`/v1/platform/plans/${id}`, body).then((r) => r.data);
  },
};

export const planCatalogQueryKeys = {
  all: () => ["platform-plan-catalog"] as const,
};

// ==================================================== Màn hình theo gói (ADR-0053)

/** Một màn hình his-web mà gói có thể gồm hoặc không gồm. */
export interface ScreenItem {
  /** Mã ổn định — thứ được lưu. Nhãn chỉ để hiển thị. */
  code: string;
  label: string;
  /** Vắng mặt với màn hình không có đường dẫn riêng (một tab bên trong màn hình khác). */
  path?: string;
  /** Mọi gói đều có: không bỏ tick được. */
  locked: boolean;
  /** Mã các màn hình phải có thì màn hình này mới dùng được. */
  requires: string[];
}

export interface ScreenGroup {
  code: string;
  label: string;
  screens: ScreenItem[];
}

/** Cảnh báo khi `screen` được tick cùng một màn hình trong `together`. Không chặn. */
export interface ScreenAdvisory {
  screen: string;
  together: string[];
  message: string;
}

/**
 * Danh mục màn hình, do máy chủ giữ. Màn hình tick vẽ đúng thứ nhận được và không tự liệt kê màn
 * hình nào, nên một màn hình mới khai ở backend tự xuất hiện ở đây.
 */
export interface ScreenCatalog {
  groups: ScreenGroup[];
  advisories: ScreenAdvisory[];
}

export const planScreenApi = {
  catalog(): Promise<ScreenCatalog> {
    return apiClient.get<ScreenCatalog>("/v1/platform/screens").then((r) => r.data);
  },
  ticks(planId: string): Promise<string[]> {
    return apiClient
      .get<{ screens: string[] }>(`/v1/platform/plans/${planId}/screens`)
      .then((r) => r.data.screens);
  },
  /**
   * Gửi TOÀN BỘ tập tick mong muốn. Máy chủ trả tập đã lưu, có thể rộng hơn tập gửi lên: nó tự
   * thêm màn hình khoá và phụ thuộc.
   */
  tick(planId: string, screens: string[]): Promise<string[]> {
    return apiClient
      .put<{ screens: string[] }>(`/v1/platform/plans/${planId}/screens`, { screens })
      .then((r) => r.data.screens);
  },
};

export const planScreenQueryKeys = {
  catalog: () => ["platform-screen-catalog"] as const,
  ticks: (planId: string) => ["platform-plan-screens", planId] as const,
};

// ==================================================== Dòng tiền vào & chính sách

/** Một lệnh thanh toán, nhìn từ phía Addy (mọi phòng khám). */
export interface PaymentFeedRow {
  id: string;
  tenantId: string;
  tenantCode: string | null;
  tenantName: string | null;
  provider: string;
  orderCode: number;
  purpose: "TOP_UP" | "SUBSCRIPTION";
  amountVnd: number;
  status: string;
  createdAt: string;
  appliedRef: string | null;
  /** null = chưa mở hoá đơn cho khoản này. Nút "Xuất hoá đơn" đọc chỗ này để tự tắt. */
  invoiceId: string | null;
  invoiceStatus: "PENDING" | "ISSUED" | "BLOCKED" | "NOT_REQUIRED" | null;
  invoiceNo: string | null;
}

export interface PaymentDailyRow {
  ngay: string;
  soDon: number;
  tongVnd: number;
}

/** Chính sách của nền tảng Addy — đúng một dòng trong DB. */
export interface PlatformSetting {
  /** Tắt = VẪN ghi nhận đủ mọi kỳ cần hoá đơn, chỉ không thúc ai đi xuất. */
  vatInvoiceEnabled: boolean;
  vatProvider: string | null;
  vatSellerTaxCode: string | null;
  note: string | null;
  updatedAt: string | null;
}

export const paymentFeedApi = {
  payments(limit = 100): Promise<PaymentFeedRow[]> {
    return apiClient
      .get<PaymentFeedRow[]>(`${BASE}/payments`, { params: { limit } })
      .then((r) => r.data);
  },
  daily(days = 14): Promise<PaymentDailyRow[]> {
    return apiClient
      .get<PaymentDailyRow[]>(`${BASE}/payments/daily`, { params: { days } })
      .then((r) => r.data);
  },
  /** Mở hoá đơn cho một khoản đã thu. Bấm lại trả về đúng hoá đơn cũ, không tạo cái thứ hai. */
  openInvoice(intentId: string): Promise<{ invoiceId: string }> {
    return apiClient
      .post<{ invoiceId: string }>(`${BASE}/payments/${intentId}/invoice`)
      .then((r) => r.data);
  },
  settings(): Promise<PlatformSetting> {
    return apiClient.get<PlatformSetting>(`${BASE}/settings`).then((r) => r.data);
  },
  updateSettings(body: Partial<PlatformSetting>): Promise<PlatformSetting> {
    return apiClient.put<PlatformSetting>(`${BASE}/settings`, body).then((r) => r.data);
  },
};

export const paymentFeedQueryKeys = {
  payments: () => ["platform-billing", "payment-feed"] as const,
  daily: () => ["platform-billing", "payment-daily"] as const,
  settings: () => ["platform-billing", "settings"] as const,
};
